import React, { useState, useEffect, useMemo } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { 
  Eye, 
  EyeOff, 
  TrendingUp, 
  Wallet, 
  Target, 
  TrendingDown, 
  ChevronLeft, 
  ChevronRight 
} from 'lucide-react';
import { FinancialTransaction, FinancialGoal, financeService, supabase } from '../../services/database/supabase';
import GoalManager from './GoalManager';
import { BudgetLimits } from './BudgetLimits';
import { toast } from 'sonner';
import { FinancialAdvisor } from './FinancialAdvisor';
import { TransactionList } from './TransactionList';
import { CashFlowChart } from './CashFlowChart';
import { FinancialAlerts } from './FinancialAlerts';
import { FinancialAnalysis } from './FinancialAnalysis';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

// Configurações globais do Chart.js
ChartJS.defaults.color = '#fff';
ChartJS.defaults.borderColor = 'rgba(255, 255, 255, 0.1)';
ChartJS.defaults.font.family = 'Inter, sans-serif';

interface FinanceDashboardProps {
  transactions: FinancialTransaction[];
  balance: number;
  isBlurred: boolean;
  onToggleBlur: () => void;
}

export const FinanceDashboard: React.FC<FinanceDashboardProps> = ({
  transactions,
  balance,
  isBlurred,
  onToggleBlur
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pieChartView, setPieChartView] = useState<'category' | 'description' | 'type'>('category');
  const [goals, setGoals] = useState<FinancialGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProjections, setShowProjections] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [viewMode, setViewMode] = useState<'total' | 'month' | 'dashboard' | 'analysis'>('month');
  const [userId, setUserId] = useState<string>('');
  const [view, setView] = useState<'analytics' | 'default'>('default');

  useEffect(() => {
    const loadUserAndGoals = async () => {
      try {
        // Obter o usuário atual do Supabase
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
          const goalsData = await financeService.getGoals();
          setGoals(goalsData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    loadUserAndGoals();
  }, []);

  const calculateCashFlowData = useMemo(() => {
    if (!transactions.length) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Receitas',
            data: [],
            borderColor: '#1AD1A5',
            backgroundColor: 'rgba(26, 209, 165, 0.1)',
            borderWidth: 2,
            fill: true
          },
          {
            label: 'Despesas',
            data: [],
            borderColor: '#FF6B6B',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            borderWidth: 2,
            fill: true
          },
          {
            label: 'Saldo',
            data: [],
            borderColor: '#4C6EF5',
            backgroundColor: 'rgba(76, 110, 245, 0.1)',
            borderWidth: 2,
            fill: true
          }
        ]
      };
    }

    // Filtrar transações baseado no período selecionado
    let filteredTransactions = [...transactions];
    if (viewMode === 'month') {
      const [year, month] = selectedDate.split('-').map(Number);
      const endDate = new Date(year, month, 0);
      
      // Incluir os últimos 6 meses até o mês selecionado
      const startDate = new Date(year, month - 6, 1);
      
      filteredTransactions = transactions.filter(t => {
        const date = new Date(t.date);
        return date >= startDate && date <= endDate;
      });
    }

    // Lógica para calcular dados do fluxo de caixa
    const periodData = new Map();
    
    // Ordenar transações por data
    const sortedTransactions = filteredTransactions.sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let runningBalance = 0;
    sortedTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const key = date.toLocaleString('pt-BR', { month: 'short', year: 'numeric' });
      if (!periodData.has(key)) {
        periodData.set(key, { income: 0, expenses: 0, balance: 0 });
      }
      
      const amount = Number(transaction.amount);
      if (transaction.type === 'Receita' || transaction.type === 'Salário') {
        periodData.get(key).income += amount;
        runningBalance += amount;
      } else if (transaction.type === 'Despesas' || transaction.type === 'Carro') {
        periodData.get(key).expenses += amount;
        runningBalance -= amount;
      }
      periodData.get(key).balance = runningBalance;
    });

    const sortedData = Array.from(periodData.entries()).sort((a, b) => {
      const dateA = new Date(a[0].split(' ')[1], ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'].indexOf(a[0].split(' ')[0]));
      const dateB = new Date(b[0].split(' ')[1], ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'].indexOf(b[0].split(' ')[0]));
      return dateA.getTime() - dateB.getTime();
    });

    // Adicionar projeções se necessário
    if (showProjections && sortedData.length > 0) {
      const lastEntry = sortedData[sortedData.length - 1];
      const lastIncome = lastEntry[1].income;
      const lastExpenses = lastEntry[1].expenses;
      const lastBalance = lastEntry[1].balance;

      // Projetar próximos 3 meses
      for (let i = 1; i <= 3; i++) {
        const lastDate = new Date(lastEntry[0].split(' ')[1], ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'].indexOf(lastEntry[0].split(' ')[0]));
        const nextDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + i);
        const key = nextDate.toLocaleString('pt-BR', { month: 'short', year: 'numeric' });
        
        const projectedIncome = lastIncome * 1.05; // 5% de aumento
        const projectedExpenses = lastExpenses * 1.03; // 3% de aumento
        const projectedBalance = lastBalance + (projectedIncome - projectedExpenses);

        sortedData.push([key, {
          income: projectedIncome,
          expenses: projectedExpenses,
          balance: projectedBalance
        }]);
      }
    }

    return {
      labels: sortedData.map(([label]) => label),
      datasets: [
        {
          label: 'Receitas',
          data: sortedData.map(([, data]) => data.income),
          borderColor: '#1AD1A5',
          backgroundColor: 'rgba(26, 209, 165, 0.1)',
          borderWidth: 2,
          fill: true
        },
        {
          label: 'Despesas',
          data: sortedData.map(([, data]) => data.expenses),
          borderColor: '#FF6B6B',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          borderWidth: 2,
          fill: true
        },
        {
          label: 'Saldo',
          data: sortedData.map(([, data]) => data.balance),
          borderColor: '#4C6EF5',
          backgroundColor: 'rgba(76, 110, 245, 0.1)',
          borderWidth: 2,
          fill: true
        }
      ]
    };
  }, [transactions, viewMode, selectedDate, showProjections]);

  const calculatePieData = useMemo(() => {
    if (!transactions.length) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 1
        }]
      };
    }

    // Filtrar transações baseado no período selecionado
    let filteredTransactions = [...transactions];
    if (viewMode === 'month') {
      const [year, month] = selectedDate.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      filteredTransactions = transactions.filter(t => {
        const date = new Date(t.date);
        return date >= startDate && date <= endDate;
      });
    }

    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD',
      '#D4A5A5', '#9A8194', '#392F5A', '#31A9B8', '#258039',
      '#FF9F1C', '#2EC4B6', '#E71D36', '#011627', '#41EAD4'
    ];

    const groupedData = filteredTransactions.reduce((acc, transaction) => {
      if (transaction.type === 'Despesas' || transaction.type === 'Carro') {
        const key = transaction[pieChartView];
        acc[key] = (acc[key] || 0) + Number(transaction.amount);
      }
      return acc;
    }, {} as Record<string, number>);

    const sortedData = Object.entries(groupedData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    return {
      labels: sortedData.map(([label]) => label),
      datasets: [{
        data: sortedData.map(([, value]) => value),
        backgroundColor: colors.slice(0, sortedData.length),
        borderColor: colors.slice(0, sortedData.length).map(color => color + '80'),
        borderWidth: 1
      }]
    };
  }, [transactions, pieChartView, viewMode, selectedDate]);

  useEffect(() => {
    const loadGoals = async () => {
      try {
        const data = await financeService.getGoals();
        setGoals(data);
      } catch (error) {
        console.error('Erro ao carregar metas:', error);
        toast.error('Erro ao carregar metas');
      }
    };

    loadGoals();
  }, []);

  // Função para navegar entre os meses
  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1);
    
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }

    setSelectedDate(
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    );
  };

  // Função para formatar o mês atual
  const formatCurrentMonth = () => {
    if (viewMode === 'total') return 'Total';
    
    const [year, month] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  };

  // Calcular valores baseados no período selecionado
  const calculatePeriodValues = () => {
    if (viewMode === 'total') {
      const totalIncome = transactions
        .filter(t => t.type === 'Receita' || t.type === 'Salário')
        .reduce((sum, t) => sum + Number(t.amount), 0);

      const totalExpenses = transactions
        .filter(t => t.type === 'Despesas' || t.type === 'Carro')
        .reduce((sum, t) => sum + Number(t.amount), 0);

      return {
        balance: totalIncome - totalExpenses,
        income: totalIncome,
        expenses: totalExpenses
      };
    }

    const [year, month] = selectedDate.split('-').map(Number);
    const currentDate = new Date(year, month - 1);
    
    // Calcular saldo acumulado até o mês anterior
    let previousBalance = 0;
    transactions.forEach(t => {
      const transDate = new Date(t.date);
      if (transDate < currentDate) {
        const amount = Number(t.amount);
        if (t.type === 'Receita' || t.type === 'Salário') {
          previousBalance += amount;
        } else if (t.type === 'Despesas' || t.type === 'Carro') {
          previousBalance -= amount;
        }
      }
    });

    // Calcular receitas e despesas do mês atual
    const filteredTransactions = transactions.filter(t => {
      const transDate = new Date(t.date);
      return transDate.getMonth() === month - 1 && 
             transDate.getFullYear() === year;
    });

    const income = filteredTransactions
      .filter(t => t.type === 'Receita' || t.type === 'Salário')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const expenses = filteredTransactions
      .filter(t => t.type === 'Despesas' || t.type === 'Carro')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    return {
      balance: previousBalance + income - expenses,
      income,
      expenses
    };
  };

  const periodValues = calculatePeriodValues();

  const handleAddGoal = async (goal: Omit<FinancialGoal, 'id' | 'created_at'>) => {
    try {
      await financeService.addGoal(goal);
      loadGoals();
    } catch (error) {
      console.error('Erro ao adicionar meta:', error);
      throw error;
    }
  };

  const handleUpdateGoal = async (id: string, updates: Partial<FinancialGoal>) => {
    try {
      await financeService.updateGoal(id, updates);
      loadGoals();
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
      throw error;
    }
  };

  const handleDeleteGoal = async (id: string) => {
    try {
      await financeService.deleteGoal(id);
      loadGoals();
    } catch (error) {
      console.error('Erro ao deletar meta:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-8">
      {view === 'analytics' ? (
        <FinancialAnalysis
          transactions={transactions}
          isBlurred={isBlurred}
        />
      ) : (
        <div className="space-y-8">
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Saldo */}
            <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <Wallet className="w-6 h-6 text-emerald-500" />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode(mode => mode === 'total' ? 'month' : 'total')}
                    className="px-3 py-1 bg-[#222] border border-white/10 rounded-lg text-sm hover:bg-white/10 transition-colors"
                  >
                    {viewMode === 'total' ? 'Ver Mês' : 'Ver Total'}
                  </button>
                  {viewMode === 'month' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="text-sm min-w-[140px] text-center">
                        {formatCurrentMonth()}
                      </span>
                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60 mb-1">Saldo</p>
                  <h3 className="text-2xl font-semibold">
                    {isBlurred ? '****' : `R$ ${periodValues.balance.toFixed(2)}`}
                  </h3>
                </div>
                <button
                  onClick={onToggleBlur}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isBlurred ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Receitas */}
            <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div>
                <p className="text-sm text-white/60 mb-1">Receitas</p>
                <h3 className="text-2xl font-semibold text-blue-500">
                  {isBlurred ? '****' : `R$ ${periodValues.income.toFixed(2)}`}
                </h3>
              </div>
            </div>

            {/* Despesas */}
            <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <TrendingDown className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <div>
                <p className="text-sm text-white/60 mb-1">Despesas</p>
                <h3 className="text-2xl font-semibold text-red-500">
                  {isBlurred ? '****' : `R$ ${periodValues.expenses.toFixed(2)}`}
                </h3>
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Fluxo de Caixa */}
            <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Fluxo de Caixa</h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowProjections(!showProjections)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      showProjections 
                        ? 'bg-blue-500/20 text-blue-500' 
                        : 'bg-white/10 text-white/60'
                    }`}
                  >
                    Projeções
                  </button>
                </div>
              </div>
              <div className="h-[300px] relative">
                <Line
                  data={calculateCashFlowData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        align: 'end' as const,
                        labels: {
                          boxWidth: 8,
                          usePointStyle: true,
                          pointStyle: 'circle',
                          padding: 15,
                          color: '#fff'
                        }
                      },
                      tooltip: {
                        mode: 'index' as const,
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        borderWidth: 1,
                        padding: 12,
                        callbacks: {
                          label: function(context) {
                            if (isBlurred) return '••••••';
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            if (context.parsed.y !== null) {
                              label += new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              }).format(context.parsed.y);
                            }
                            return label;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(255, 255, 255, 0.05)',
                          drawBorder: false
                        },
                        ticks: { 
                          color: '#8E95A3',
                          padding: 10,
                          callback: function(value) {
                            if (isBlurred) return '••••';
                            return new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              notation: 'compact',
                              maximumFractionDigits: 1
                            }).format(value as number);
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        },
                        ticks: {
                          color: '#8E95A3',
                          padding: 5,
                          maxRotation: 0,
                          autoSkipPadding: 15
                        }
                      }
                    },
                    interaction: {
                      mode: 'nearest',
                      axis: 'x',
                      intersect: false
                    },
                    elements: {
                      line: {
                        tension: 0.4
                      },
                      point: {
                        radius: 0,
                        hoverRadius: 6
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* Gastos por Categoria */}
            <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">
                  Gastos por {
                    pieChartView === 'category' ? 'Categoria' :
                    pieChartView === 'description' ? 'Descrição' : 'Tipo'
                  }
                </h3>
                <select
                  value={pieChartView}
                  onChange={(e) => setPieChartView(e.target.value as 'category' | 'description' | 'type')}
                  className="bg-[#222] border border-white/10 rounded-lg px-3 py-1 text-sm"
                >
                  <option value="category">Categoria</option>
                  <option value="description">Descrição</option>
                  <option value="type">Tipo</option>
                </select>
              </div>
              <div className="h-[300px] flex items-center justify-center">
                <Pie
                  data={calculatePieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right' as const,
                        align: 'center' as const,
                        labels: { 
                          color: '#fff',
                          font: { size: 11 },
                          boxWidth: 15,
                          padding: 15,
                          usePointStyle: true,
                          pointStyle: 'circle'
                        }
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        borderWidth: 1,
                        padding: 12,
                        callbacks: {
                          label: function(context) {
                            if (isBlurred) return '••••••';
                            const label = context.label || '';
                            const value = context.raw as number;
                            const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${label}: R$ ${value.toFixed(2)} (${percentage}%)`;
                          }
                        }
                      }
                    },
                    cutout: '60%',
                    radius: '90%'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Insights e Assistente */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alertas e Insights Financeiros */}
            <div className="space-y-6">
              <FinancialAlerts
                transactions={transactions}
                isBlurred={isBlurred}
              />
            </div>

            {/* Lista de Transações */}
            <TransactionList
              transactions={transactions}
              isBlurred={isBlurred}
              viewMode={viewMode}
              selectedDate={selectedDate}
            />
          </div>

          {/* Metas */}
          {userId && (
            <GoalManager userId={userId} />
          )}
        </div>
      )}
    </div>
  );
};
